import type { SignatureValue } from "./types/file-mapping-types";
import { FormField } from "@/lib/database/forms";
import { getCountryDialCodeById } from "@/lib/db/countries-dial-codes";

/**
 * Normalizes a field key/label for case- and whitespace/underscore-insensitive matching between a
 * grid column key (derived from `response_data`) and a form field's `label`. Shared so the admin
 * Responses table's editable-column check and the inline-edit server action's field validation
 * can never drift into using two different comparators for "is this the same field".
 */
export const normalize = (str: string) => str.toLowerCase().replace(/[\s_]/g, "");

/**
 * Format a phone object { phone, countryDialCodeId } into a display string like "+1 9495379675"
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const formatPhoneValue = (value: any): string => {
  if (!value || typeof value !== "object" || !("phone" in value)) return "";
  const phone = value.phone || "";
  if (!phone) return "";
  const dialCodeId = value.countryDialCodeId;
  if (dialCodeId) {
    const country = getCountryDialCodeById(dialCodeId);
    if (country) return `+${country.dial_code} ${phone}`;
  }
  return phone;
};

export const formatFieldName = (field: string): string => {
  return field.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
};

export const sortFieldsByFormOrder = (fields: string[], formFields: FormField[], pinnedFields?: string[]): string[] => {
  if (pinnedFields && pinnedFields.length > 0) {
    const pinnedNormalized = new Set(pinnedFields.map(normalize));
    const pinnedMap = new Map<string, string>();
    const regular: string[] = [];

    for (const field of fields) {
      const norm = normalize(field);
      if (pinnedNormalized.has(norm)) {
        pinnedMap.set(norm, field);
      } else {
        regular.push(field);
      }
    }

    // Preserve the order defined in pinnedFields
    const pinned = pinnedFields.map((pf) => pinnedMap.get(normalize(pf))).filter(Boolean) as string[];

    const formOrderMap = new Map<string, number>();
    formFields.forEach((f, i) => {
      if (!pinnedNormalized.has(normalize(f.label))) {
        formOrderMap.set(normalize(f.label), i);
      }
    });

    regular.sort((a, b) => {
      const aIdx = formOrderMap.get(normalize(a));
      const bIdx = formOrderMap.get(normalize(b));
      if (aIdx !== undefined && bIdx !== undefined) return aIdx - bIdx;
      if (aIdx !== undefined) return -1;
      if (bIdx !== undefined) return 1;
      return a.localeCompare(b);
    });

    return [...pinned, ...regular];
  }

  // Simple form order sorting without pinned fields
  const formOrderMap = new Map<string, number>();
  formFields.forEach((f, i) => {
    formOrderMap.set(normalize(f.label), i);
  });

  return fields.sort((a, b) => {
    const aIdx = formOrderMap.get(normalize(a));
    const bIdx = formOrderMap.get(normalize(b));
    if (aIdx !== undefined && bIdx !== undefined) return aIdx - bIdx;
    if (aIdx !== undefined) return -1;
    if (bIdx !== undefined) return 1;
    return a.localeCompare(b);
  });
};

export const formatTimestampValue = (value: string): string => {
  const date = new Date(value);
  const options: Intl.DateTimeFormatOptions = {
    month: "2-digit",
    day: "2-digit",
    year: "2-digit",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  };
  const formatted = date.toLocaleString("en-US", options);
  const [datePart, timePart] = formatted.split(", ");
  const [month, day, year] = datePart.split("/");
  const [time, ampm] = timePart.split(" ");
  return `${month}/${day}/${year}, ${time} ${ampm}`;
};

/** Sentinel stored as a choice's value when a respondent selects the built-in "Other" option. */
export const OTHER_OPTION_VALUE = "__OTHER__";

/**
 * Label shown for the "Other" option, and the display fallback when a respondent selected
 * "Other" but left the "Please specify" free-text blank — without it the raw
 * `{ value: "__OTHER__", otherText: "" }` object stringifies to "[object Object]".
 */
export const OTHER_OPTION_LABEL = "Other";

/**
 * Type guard for the object shape persisted when a respondent picks "Other" on a choice field:
 * `{ value: "__OTHER__", otherText?: string }`.
 *
 * @param value - Any raw response value.
 * @returns True when `value` is a selected-"Other" option object.
 */
export const isOtherOption = (value: unknown): value is { value: string; otherText?: string } =>
  !!value && typeof value === "object" && (value as { value?: unknown }).value === OTHER_OPTION_VALUE;

/**
 * Resolve a selected-"Other" option object to display text: the respondent's free-text when
 * present, otherwise the {@link OTHER_OPTION_LABEL} — never the "[object Object]" that a bare
 * `String()` of the object produces.
 *
 * @param value - A selected-"Other" option object (see {@link isOtherOption}).
 * @returns The free-text answer, or "Other" when it was left blank.
 */
export const formatOtherOption = (value: { otherText?: string }): string =>
  value.otherText ? String(value.otherText) : OTHER_OPTION_LABEL;

/**
 * Type guard for the object a signature field stores: `{ kind: "type" | "draw", value: string }`.
 *
 * @param value - Any raw response value.
 * @returns True when `value` is a signature value.
 */
const isSignatureValue = (value: unknown): value is SignatureValue => {
  if (!value || typeof value !== "object") return false;
  const { kind, value: inner } = value as { kind?: unknown; value?: unknown };
  return (kind === "type" || kind === "draw") && typeof inner === "string";
};

/** Text shown for a signature answer; the signature itself is an image or typed name, not a table value. */
const SIGNATURE_DISPLAY_LABEL = "Signed";

export const formatArrayItem = (item: any): string => {
  if (isOtherOption(item)) return formatOtherOption(item);
  return String(item);
};

export const formatValue = (value: any, formField?: FormField): string => {
  if (value === null || value === undefined || value === "") return "";
  if (Array.isArray(value)) {
    return value.map(formatArrayItem).join(", ");
  }
  if (isOtherOption(value)) return formatOtherOption(value);
  if (value && typeof value === "object" && "phone" in value) {
    return formatPhoneValue(value);
  }
  if (isSignatureValue(value)) return SIGNATURE_DISPLAY_LABEL;
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (formField?.type === "date" && typeof value === "string" && value.match(/^\d{4}-\d{2}-\d{2}$/)) {
    const [year, month, day] = value.split("-");
    return new Date(parseInt(year), parseInt(month) - 1, parseInt(day)).toLocaleDateString();
  }
  return String(value);
};

export const calculatePercentage = (value: number, total: number): number => {
  return Number(((value / total) * 100).toFixed(1));
};
